'use client';

import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { signIn, useSession } from 'next-auth/react';
import { type MouseEvent, type ReactElement, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import type { MouseEventHandler } from 'react';

import { useActivityListener } from './use-activity-listeners';
import { OktaPromptParameterValues } from './OktaSignInButton';

export default function AuthButton({ prompt }: { prompt?: OktaPromptParameterValues }): ReactElement {
    const searchParams = useSearchParams();
    const callbackUrl = searchParams.get('callbackUrl') ?? '/dashboard';
    const router = useRouter();
    const { data: session, status } = useSession();
    const { minutesRemaining } = useActivityListener();
    if (minutesRemaining === 0) {
        console.log(`[Auth Button] Session Timed Out`);
    }
    const loading = status === 'loading';

    useEffect(() => {
        if (session?.err === 'RefreshAccessTokenError') {
            console.warn('RefreshAccessTokenError encountered. Forcing user to reauthenticate.');
            void signIn('okta', undefined, { prompt: 'login' });
        }
    }, [session]);
    const theme = useTheme();
    const [anchorElUser, setAnchorElUser] = useState<HTMLElement | null>(null);

    if (loading) {
        return (
            <>
                <CircularProgress color="success" />
            </>
        );
    }

    const userInitials =
        session?.user?.name
            ?.split(' ')
            .map(splitNameChunk => splitNameChunk[0])
            .join('')
            .trim() ?? '';

    const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = (_: MouseEvent<HTMLElement>) => {
        setAnchorElUser(null);
    };

    const handleSignIn: MouseEventHandler<HTMLButtonElement> = e => {
        e?.preventDefault();
        void signIn('okta', { callbackUrl: callbackUrl }, !prompt ? {} : { prompt });
    };

    // TODO: update for okta redirect flow, it's simplified for now
    const handleSignOut = (_?: MouseEvent) => {
        router.push('/auth/signout');
    };

    return (
        <>
            {session?.user ? (
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar alt={userInitials} sx={{ bgcolor: theme.palette.secondary.main }} data-cy="avatar">
                        {userInitials}
                    </Avatar>
                </IconButton>
            ) : (
                <Button data-cy="oktaSignInButton" variant="contained" color="secondary" onClick={handleSignIn}>
                    Sign in
                </Button>
            )}
            <Menu
                data-cy="userSettingsMenu"
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <MenuItem onClick={handleSignOut} data-cy="logout">
                    <Typography textAlign="center">Log out</Typography>
                </MenuItem>
            </Menu>
        </>
    );
}
